import { TFunction } from 'i18next';
import { AppLanguage } from '../types/Language';
import { OpeningHoursEntry, OpeningTime, OpeningTimeRecurrence } from '../types/Location';
import { hasLocalizedText, serializeLocalizedText } from './localizedText';
import { DayValue } from './dayUtils';

export function isDefaultRecurrence(recurrence?: OpeningTimeRecurrence): boolean {
  if (!recurrence) {
    return true;
  }

  const frequency = recurrence.frequency ?? 'weekly';
  const interval = recurrence.interval ?? 1;

  return (
    frequency === 'weekly' &&
    interval === 1 &&
    !recurrence.startDate &&
    !recurrence.endDate &&
    recurrence.monthlyPosition === undefined
  );
}

export function serializeRecurrence(
  recurrence?: OpeningTimeRecurrence
): OpeningTimeRecurrence | undefined {
  if (!recurrence || isDefaultRecurrence(recurrence)) {
    return undefined;
  }

  const frequency = recurrence.frequency ?? 'weekly';
  const result: OpeningTimeRecurrence = { frequency };

  const interval = recurrence.interval ?? 1;
  if (interval > 1) {
    result.interval = interval;
  }

  if (recurrence.startDate) {
    result.startDate = recurrence.startDate;
  }

  if (recurrence.endDate) {
    result.endDate = recurrence.endDate;
  }

  if (frequency === 'monthly') {
    const monthlyPosition = recurrence.monthlyPosition ?? 1;
    result.monthlyPosition = monthlyPosition === 0 ? 1 : monthlyPosition;
  }

  return result;
}

export function serializeOpeningTimes(
  entries: OpeningHoursEntry[],
  languages?: AppLanguage[]
): OpeningTime[] {
  return entries.map(({ id, day, from, to, recurrence, note }) => {
    const result: OpeningTime = { day, from, to };

    const serializedRecurrence = serializeRecurrence(recurrence);
    if (serializedRecurrence) {
      result.recurrence = serializedRecurrence;
    }

    if (note && hasLocalizedText(note)) {
      result.note = languages
        ? serializeLocalizedText(note, languages, { omitEmptyOptional: true })
        : note;
    }

    return result;
  });
}

export function formatRecurrenceSummary(
  recurrence: OpeningTimeRecurrence | undefined,
  day: DayValue,
  t: TFunction
): string | null {
  if (!recurrence || isDefaultRecurrence(recurrence)) {
    return null;
  }

  const frequency = recurrence.frequency ?? 'weekly';
  const interval = recurrence.interval ?? 1;
  const dayLabel = t(`openingHours.${day}`);

  if (frequency === 'once') {
    return recurrence.startDate
      ? t('openingHoursTable.recurrenceSummary.once', { date: recurrence.startDate })
      : t('openingHoursTable.recurrenceSummary.onceNoDate');
  }

  if (frequency === 'monthly') {
    const positionKey =
      recurrence.monthlyPosition !== undefined
        ? String(recurrence.monthlyPosition)
        : '1';
    const positionLabel = t(`openingHoursTable.monthlyPositions.${positionKey}`, {
      day: dayLabel,
    });
    let summary = t('openingHoursTable.recurrenceSummary.monthly', {
      position: positionLabel,
    });
    if (interval > 1) {
      summary = t('openingHoursTable.recurrenceSummary.monthlyInterval', {
        interval,
        summary,
      });
    }
    return appendDateRange(summary, recurrence, t);
  }

  let summary =
    interval > 1
      ? t('openingHoursTable.recurrenceSummary.weeklyInterval', { interval, day: dayLabel })
      : t('openingHoursTable.recurrenceSummary.weekly', { day: dayLabel });

  return appendDateRange(summary, recurrence, t);
}

function appendDateRange(
  summary: string,
  recurrence: OpeningTimeRecurrence,
  t: TFunction
): string {
  if (recurrence.startDate && recurrence.endDate) {
    return `${summary} (${t('openingHoursTable.recurrenceSummary.dateRange', {
      start: recurrence.startDate,
      end: recurrence.endDate,
    })})`;
  }

  if (recurrence.endDate) {
    return `${summary} (${t('openingHoursTable.recurrenceSummary.untilDate', {
      date: recurrence.endDate,
    })})`;
  }

  return summary;
}

export function hasOpeningTimeExtras(entry: OpeningHoursEntry): boolean {
  return !isDefaultRecurrence(entry.recurrence) || hasLocalizedText(entry.note);
}
